import React, { useState, useEffect, useMemo } from 'react';
import { AlertTriangle, Check, Loader2, Save, Trash2, X, Search, Settings } from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { stcBinService } from '../../../../services/stcBinService';
import { stcBinSizeService } from '../../../../services/stcBinSizeService';
import { stcBinTypeService, StcBinTypeDto } from '../../../../services/stcBinTypeService';
import { stcBinGrpService, StcBinGrpDto } from '../../../../services/stcBinGrpService';
import { useStatusOptions } from '../../../common/statusPresentation';
import { stcZoneService, StcZoneDto } from '../../../../services/stcZoneService';
import { stcPriorityService, StcPriorityDto } from '../../../../services/stcPriorityService';
import { stcResourceService, StcResourceDto } from '../../../../services/stcResourceService';
import { itemsService } from '../../../../services/itemsService';
import { ItemsDto } from '../../../../types/items';
import { StcBinSizeDto } from '../../../../types/stcBinSize';
import { StcBinDto } from '../../../../types/stcBin';
import { BulkUpdateBinsRequest, BulkDeleteBinsRequest, FilterOptions, BinLocation, BulkUndoData, BulkUndoEntry } from './types';

interface BulkUpdateModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedBinCodes: string[];
    warehouseID: string;
    filterOptions: FilterOptions | null;
    onUpdateSuccess: () => void;
    onDeleteSuccess: () => void;
    /** Optional: bin data for pre-filling when single record is selected */
    selectedBinData?: StcBinDto[];
    /** Valgte lokasjoner m/nåværende verdier — gir før/etter-forhåndsvisning + angre-data */
    selectedLocations?: BinLocation[];
    /** Kalles etter vellykket oppdatering med angre-data (før-verdier per bin) */
    onUpdateApplied?: (undo: BulkUndoData) => void;
}

interface UpdateFields {
    binSizeNo: boolean;
    binTypeNo: boolean;
    resourceNo: boolean;
    binGrpNo: boolean;
    stcBinCatNo: boolean;
    stcZoneID: boolean;
    pickPriorityNo: boolean;
    pickableYesNo: boolean;
    reservationAllowed: boolean;
    autoReplenishment: boolean;
    itemNo: boolean;
    replenishMinLevel: boolean;
    replenishMaxLevel: boolean;
    customSortID: boolean;
    putAwayPriorityNo: boolean;
    negativeReservationAllowed: boolean;
    sharedBinYesNo: boolean;
    sharedBinMaxQuantity: boolean;
    sharedBinPreferEmptyBin: boolean;
    barCodeConfirmLocation: boolean;
}

type TabType = 'basics' | 'priority' | 'reservation' | 'replenishment' | 'shared';

/**
 * Bulk update modal with tabbed interface (5 tabs)
 * Replaces BulkUpdatePanel with a modern modal design
 */
export const BulkUpdateModal: React.FC<BulkUpdateModalProps> = ({
    isOpen,
    onClose,
    selectedBinCodes,
    warehouseID,
    filterOptions,
    onUpdateSuccess,
    onDeleteSuccess,
    selectedBinData,
    selectedLocations,
    onUpdateApplied
}) => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const companyNo = user?.companyNo || '';

    // Detect single selection for pre-filling
    const isSingleSelection = selectedBinCodes.length === 1;
    const singleBin = isSingleSelection && selectedBinData?.length === 1 ? selectedBinData[0] : null;

    const [activeTab, setActiveTab] = useState<TabType>('basics');

    // Real data from API
    const [binSizes, setBinSizes] = useState<StcBinSizeDto[]>([]);
    const [binTypes, setBinTypes] = useState<StcBinTypeDto[]>([]);
    const [binGroups, setBinGroups] = useState<StcBinGrpDto[]>([]);
    // Lokasjonskategorier (ST42) fra master-StatusCodes — språkbevisst (Model A). Samme lokale
    // form som før ({stcBinCatNo, lng_1}) så render-logikken under er uendret.
    const binCategoryStatusOptions = useStatusOptions(42);
    const binCategories = useMemo(
        () => binCategoryStatusOptions.map(o => ({ stcBinCatNo: o.value, lng_1: o.text })),
        [binCategoryStatusOptions]
    );
    const [zones, setZones] = useState<StcZoneDto[]>([]);
    const [priorities, setPriorities] = useState<StcPriorityDto[]>([]);
    const [resources, setResources] = useState<StcResourceDto[]>([]);
    const [isLoadingBinSizes, setIsLoadingBinSizes] = useState(false);
    const [isLoadingBinTypes, setIsLoadingBinTypes] = useState(false);
    const [isLoadingBinGroups, setIsLoadingBinGroups] = useState(false);
    const [isLoadingZones, setIsLoadingZones] = useState(false);
    const [isLoadingPriorities, setIsLoadingPriorities] = useState(false);
    const [isLoadingResources, setIsLoadingResources] = useState(false);

    // Item search state
    const [itemNo, setItemNo] = useState<string>(singleBin?.itemNo ?? '');
    const [selectedItem, setSelectedItem] = useState<ItemsDto | null>(null);
    const [itemSearchTerm, setItemSearchTerm] = useState<string>(singleBin?.itemNo ?? '');
    const [itemSearchResults, setItemSearchResults] = useState<ItemsDto[]>([]);
    const [isSearchingItems, setIsSearchingItems] = useState(false);
    const [showItemDropdown, setShowItemDropdown] = useState(false);

    // Track which fields should be updated (enable all when single selection for pre-fill)
    const [updateFields, setUpdateFields] = useState<UpdateFields>({
        binSizeNo: isSingleSelection,
        binTypeNo: isSingleSelection,
        resourceNo: isSingleSelection,
        binGrpNo: isSingleSelection,
        stcBinCatNo: isSingleSelection,
        stcZoneID: isSingleSelection,
        pickPriorityNo: isSingleSelection,
        pickableYesNo: isSingleSelection,
        reservationAllowed: isSingleSelection,
        autoReplenishment: isSingleSelection,
        itemNo: isSingleSelection,
        replenishMinLevel: isSingleSelection,
        replenishMaxLevel: isSingleSelection,
        customSortID: isSingleSelection,
        putAwayPriorityNo: isSingleSelection,
        negativeReservationAllowed: isSingleSelection,
        sharedBinYesNo: isSingleSelection,
        sharedBinMaxQuantity: isSingleSelection,
        sharedBinPreferEmptyBin: isSingleSelection,
        barCodeConfirmLocation: isSingleSelection
    });

    // Field values (pre-fill from singleBin when single selection)
    const [binSizeNo, setBinSizeNo] = useState<number>(singleBin?.binSizeNo ?? 1);
    const [binTypeNo, setBinTypeNo] = useState<number>(singleBin?.binTypeNo ?? 1);
    const [resourceNo, setResourceNo] = useState<number | undefined>(singleBin?.resourceNo ?? undefined);
    const [binGrpNo, setBinGrpNo] = useState<number | undefined>(singleBin?.binGrpNo ?? undefined);
    const [stcBinCatNo, setStcBinCatNo] = useState<number | undefined>(singleBin?.stcBinCatNo ?? undefined);
    // Default til første kategori når ST42-opsjonene er lastet (tidligere satt i load-effekten; ikke ved singleBin).
    useEffect(() => {
        if (binCategories.length > 0 && !singleBin) {
            setStcBinCatNo(prev => prev ?? binCategories[0].stcBinCatNo);
        }
    }, [binCategories, singleBin]);
    const [stcZoneID, setStcZoneID] = useState<string>(singleBin?.stcZoneID ?? '001');
    const [pickPriorityNo, setPickPriorityNo] = useState<number | undefined>(singleBin?.pickPriorityNo ?? 50);
    const [pickableYesNo, setPickableYesNo] = useState<boolean>(singleBin?.pickableYesNo ?? true);
    const [reservationAllowed, setReservationAllowed] = useState<boolean>(singleBin?.reservationAllowed ?? true);
    const [autoReplenishment, setAutoReplenishment] = useState<boolean>(singleBin?.autoReplenishment ?? false);
    const [replenishMinLevel, setReplenishMinLevel] = useState<number>(singleBin?.replenishMinLevel ?? 0);
    const [replenishMaxLevel, setReplenishMaxLevel] = useState<number>(singleBin?.replenishMaxLevel ?? 0);
    const [customSortID, setCustomSortID] = useState<number>(singleBin?.customSortID ?? 1000);
    const [putAwayPriorityNo, setPutAwayPriorityNo] = useState<number | undefined>(singleBin?.putAwayPriorityNo ?? 50);
    const [negativeReservationAllowed, setNegativeReservationAllowed] = useState<boolean>(singleBin?.negativeReservationAllowed ?? false);
    const [sharedBinYesNo, setSharedBinYesNo] = useState<boolean>(singleBin?.sharedBinYesNo ?? false);
    const [sharedBinMaxQuantity, setSharedBinMaxQuantity] = useState<number>(singleBin?.sharedBinMaxQuantity ?? 0);
    const [sharedBinPreferEmptyBin, setSharedBinPreferEmptyBin] = useState<boolean>(singleBin?.sharedBinPreferEmptyBin ?? false);
    // Tri-state (TW 36344429): '' = arv fra profil, 'true'/'false' = overstyr
    const [barCodeConfirmLocation, setBarCodeConfirmLocation] = useState<string>(
        singleBin?.barCodeConfirmLocation === true ? 'true'
            : singleBin?.barCodeConfirmLocation === false ? 'false'
            : ''
    );

    // State
    const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [notification, setNotification] = useState<{
        type: 'success' | 'error' | 'warning';
        message: string;
    } | null>(null);

    // Load data on mount
    useEffect(() => {
        if (!isOpen || !companyNo) return;

        const loadData = async () => {
            // Load bin sizes
            setIsLoadingBinSizes(true);
            try {
                const sizes = await stcBinSizeService.getByCompanyNo(companyNo);
                setBinSizes(sizes);
                if (sizes.length > 0 && !singleBin) {
                    setBinSizeNo(sizes[0].binSizeNo);
                }
            } catch (err) {
                console.error('Failed to load bin sizes:', err);
            } finally {
                setIsLoadingBinSizes(false);
            }

            // Load bin types
            setIsLoadingBinTypes(true);
            try {
                const types = await stcBinTypeService.getByCompanyNo(companyNo);
                setBinTypes(types);
                if (types.length > 0 && !singleBin) {
                    setBinTypeNo(types[0].binTypeNo);
                }
            } catch (err) {
                console.error('Failed to load bin types:', err);
            } finally {
                setIsLoadingBinTypes(false);
            }

            // Load bin groups
            setIsLoadingBinGroups(true);
            try {
                const groups = await stcBinGrpService.getByCompanyNo(companyNo);
                setBinGroups(groups);
                if (groups.length > 0 && !singleBin) {
                    setBinGrpNo(groups[0].binGrpNo);
                }
            } catch (err) {
                console.error('Failed to load bin groups:', err);
            } finally {
                setIsLoadingBinGroups(false);
            }

            // Lokasjonskategorier lastes via useStatusOptions(42) — default settes i egen effekt under.

            // Load zones
            setIsLoadingZones(true);
            try {
                const zoneList = await stcZoneService.getByCompanyNo(companyNo);
                setZones(zoneList);
                if (zoneList.length > 0 && !singleBin) {
                    setStcZoneID(zoneList[0].stcZoneID);
                }
            } catch (err) {
                console.error('Failed to load zones:', err);
            } finally {
                setIsLoadingZones(false);
            }

            // Load priorities
            setIsLoadingPriorities(true);
            try {
                const priorityList = await stcPriorityService.getByCompanyNo(companyNo);
                setPriorities(priorityList);
                if (priorityList.length > 0 && !singleBin) {
                    setPickPriorityNo(priorityList[0].priorityNo);
                    setPutAwayPriorityNo(priorityList[0].priorityNo);
                }
            } catch (err) {
                console.error('Failed to load priorities:', err);
            } finally {
                setIsLoadingPriorities(false);
            }

            // Load resources
            setIsLoadingResources(true);
            try {
                const resourceList = await stcResourceService.getByCompanyNo(companyNo);
                setResources(resourceList);
                if (resourceList.length > 0 && !singleBin) {
                    setResourceNo(resourceList[0].resourceNo);
                }
            } catch (err) {
                console.error('Failed to load resources:', err);
            } finally {
                setIsLoadingResources(false);
            }
        };

        loadData();
    }, [isOpen, companyNo]);

    // Search items with debounce
    useEffect(() => {
        const searchItems = async () => {
            if (!itemSearchTerm || itemSearchTerm.length < 2) {
                setItemSearchResults([]);
                setShowItemDropdown(false);
                return;
            }

            setIsSearchingItems(true);
            try {
                const results = await itemsService.searchCurrentItems(itemSearchTerm);
                setItemSearchResults(results);
                setShowItemDropdown(results.length > 0);
            } catch (err) {
                console.error('Failed to search items:', err);
                setItemSearchResults([]);
                setShowItemDropdown(false);
            } finally {
                setIsSearchingItems(false);
            }
        };

        const timeoutId = setTimeout(searchItems, 300);
        return () => clearTimeout(timeoutId);
    }, [itemSearchTerm]);

    // Reset fields when selection changes
    useEffect(() => {
        if (!isOpen) return;

        setUpdateFields({
            binSizeNo: false,
            binTypeNo: false,
            resourceNo: false,
            binGrpNo: false,
            stcBinCatNo: false,
            stcZoneID: false,
            pickPriorityNo: false,
            pickableYesNo: false,
            reservationAllowed: false,
            autoReplenishment: false,
            itemNo: false,
            replenishMinLevel: false,
            replenishMaxLevel: false,
            customSortID: false,
            putAwayPriorityNo: false,
            negativeReservationAllowed: false,
            sharedBinYesNo: false,
            sharedBinMaxQuantity: false,
            sharedBinPreferEmptyBin: false,
            barCodeConfirmLocation: false
        });
        setNotification(null);
        setActiveTab('basics');
    }, [selectedBinCodes, isOpen]);

    // Auto-hide notification after 5 seconds
    useEffect(() => {
        if (notification) {
            const timer = setTimeout(() => setNotification(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [notification]);

    // Handle item selection from dropdown
    const handleSelectItem = (item: ItemsDto) => {
        setSelectedItem(item);
        setItemNo(item.itemNo);
        setItemSearchTerm(item.itemNo);
        setShowItemDropdown(false);
    };

    // Clear selected item
    const handleClearItem = () => {
        setSelectedItem(null);
        setItemNo('');
        setItemSearchTerm('');
        setItemSearchResults([]);
        setShowItemDropdown(false);
    };

    // Check if at least one field is selected for update
    const hasSelectedFields = (): boolean => {
        return Object.values(updateFields).some(v => v);
    };

    // Build update request with only selected fields
    const buildUpdateRequest = (): BulkUpdateBinsRequest => {
        const request: BulkUpdateBinsRequest = {
            companyNo,
            warehouseID,
            binCodes: selectedBinCodes
        };

        if (updateFields.binSizeNo) request.binSizeNo = binSizeNo;
        if (updateFields.binTypeNo) request.binTypeNo = binTypeNo;
        if (updateFields.resourceNo) request.resourceNo = resourceNo;
        if (updateFields.binGrpNo) request.binGrpNo = binGrpNo;
        if (updateFields.stcBinCatNo) request.stcBinCatNo = stcBinCatNo;
        if (updateFields.stcZoneID) request.stcZoneID = stcZoneID;
        if (updateFields.pickPriorityNo) request.pickPriorityNo = pickPriorityNo;
        if (updateFields.pickableYesNo) request.pickableYesNo = pickableYesNo;
        if (updateFields.reservationAllowed) request.reservationAllowed = reservationAllowed;
        if (updateFields.autoReplenishment) request.autoReplenishment = autoReplenishment;
        if (updateFields.itemNo) request.itemNo = itemNo;
        if (updateFields.replenishMinLevel) request.replenishMinLevel = replenishMinLevel;
        if (updateFields.replenishMaxLevel) request.replenishMaxLevel = replenishMaxLevel;
        if (updateFields.customSortID) request.customSortID = customSortID;
        if (updateFields.putAwayPriorityNo) request.putAwayPriorityNo = putAwayPriorityNo;
        if (updateFields.negativeReservationAllowed) request.negativeReservationAllowed = negativeReservationAllowed;
        if (updateFields.sharedBinYesNo) request.sharedBinYesNo = sharedBinYesNo;
        if (updateFields.sharedBinMaxQuantity) request.sharedBinMaxQuantity = sharedBinMaxQuantity;
        if (updateFields.sharedBinPreferEmptyBin) request.sharedBinPreferEmptyBin = sharedBinPreferEmptyBin;
        // Tri-state: tomt valg = sett NULL (arv fra profil) — backend tolker 'inherit' som NULL
        if (updateFields.barCodeConfirmLocation) request.barCodeConfirmLocation = barCodeConfirmLocation === '' ? 'inherit' : barCodeConfirmLocation;

        return request;
    };

    // Get descriptions for display
    const getBinSizeDescription = (sizeNo: number): string => {
        const size = binSizes.find(s => s.binSizeNo === sizeNo);
        return size ? `${sizeNo} - ${size.description1}` : `${sizeNo}`;
    };

    const getBinTypeDescription = (typeNo: number): string => {
        const type = binTypes.find(t => t.binTypeNo === typeNo);
        return type ? `${typeNo} - ${type.description1 || 'Bin Type'}` : `${typeNo}`;
    };

    const getBinGrpDescription = (grpNo: number): string => {
        const group = binGroups.find(g => g.binGrpNo === grpNo);
        return group ? `${grpNo} - ${group.description1}` : `${grpNo}`;
    };

    const getBinCategoryDescription = (catNo: number): string => {
        const category = binCategories.find(c => c.stcBinCatNo === catNo);
        return category ? `${catNo} - ${category.lng_1}` : `${catNo}`;
    };

    const getZoneDescription = (zoneID: string): string => {
        const zone = zones.find(z => z.stcZoneID === zoneID);
        return zone ? `${zoneID} - ${zone.description1}` : zoneID;
    };

    const getPriorityDescription = (priorityNo: number): string => {
        const priority = priorities.find(p => p.priorityNo === priorityNo);
        return priority ? `${priorityNo} - ${priority.description1}` : `${priorityNo}`;
    };

    const getResourceDescription = (resourceNo: number): string => {
        const resource = resources.find(r => r.resourceNo === resourceNo);
        return resource ? `${resourceNo} - ${resource.description1}` : `${resourceNo}`;
    };

    // Get summary of fields that will be updated
    const getUpdateSummary = (): string[] => {
        const summary: string[] = [];

        const none = t('manager.modules.rackBuilder.bulk.none', {}, 'Ingen');
        const yes = t('manager.modules.rackBuilder.common.yes', {}, 'Ja');
        const no = t('manager.modules.rackBuilder.common.no', {}, 'Nei');

        if (updateFields.binSizeNo) summary.push(t('manager.modules.rackBuilder.bulk.summaryBinSize', { '0': getBinSizeDescription(binSizeNo) }, 'Bin Storrelse: {0}'));
        if (updateFields.binTypeNo) summary.push(t('manager.modules.rackBuilder.bulk.summaryBinType', { '0': getBinTypeDescription(binTypeNo) }, 'Bin Type: {0}'));
        if (updateFields.resourceNo) summary.push(t('manager.modules.rackBuilder.bulk.summaryResource', { '0': resourceNo ? getResourceDescription(resourceNo) : none }, 'Ressurs: {0}'));
        if (updateFields.binGrpNo) summary.push(t('manager.modules.rackBuilder.bulk.summaryBinGroup', { '0': binGrpNo ? getBinGrpDescription(binGrpNo) : none }, 'Bin Gruppe: {0}'));
        if (updateFields.stcBinCatNo) summary.push(t('manager.modules.rackBuilder.bulk.summaryBinCategory', { '0': stcBinCatNo ? getBinCategoryDescription(stcBinCatNo) : none }, 'Lokasjonskategori: {0}'));
        if (updateFields.stcZoneID) summary.push(t('manager.modules.rackBuilder.bulk.summaryZone', { '0': getZoneDescription(stcZoneID) }, 'Sone: {0}'));
        if (updateFields.pickPriorityNo) summary.push(t('manager.modules.rackBuilder.bulk.summaryPickPriority', { '0': pickPriorityNo ? getPriorityDescription(pickPriorityNo) : none }, 'Plukk Prioritet: {0}'));
        if (updateFields.pickableYesNo) summary.push(t('manager.modules.rackBuilder.bulk.summaryPickable', { '0': pickableYesNo ? yes : no }, 'Plukkbar: {0}'));
        if (updateFields.reservationAllowed) summary.push(t('manager.modules.rackBuilder.bulk.summaryReservation', { '0': reservationAllowed ? yes : no }, 'Reservasjon: {0}'));
        if (updateFields.autoReplenishment) summary.push(t('manager.modules.rackBuilder.bulk.summaryAutoReplenishment', { '0': autoReplenishment ? yes : no }, 'Auto-pafylling: {0}'));
        if (updateFields.itemNo) {
            const itemDescription = selectedItem ? `${itemNo} - ${selectedItem.description1}` : itemNo;
            summary.push(t('manager.modules.rackBuilder.bulk.summaryItemNo', { '0': itemDescription }, 'Varenummer: {0}'));
        }
        if (updateFields.replenishMinLevel) summary.push(t('manager.modules.rackBuilder.bulk.summaryMinReplenish', { '0': replenishMinLevel }, 'Min pafyllniva: {0}'));
        if (updateFields.replenishMaxLevel) summary.push(t('manager.modules.rackBuilder.bulk.summaryMaxReplenish', { '0': replenishMaxLevel }, 'Maks pafyllniva: {0}'));
        if (updateFields.customSortID) summary.push(t('manager.modules.rackBuilder.bulk.summaryCustomSortId', { '0': customSortID }, 'Sortering ID: {0}'));
        if (updateFields.putAwayPriorityNo) summary.push(t('manager.modules.rackBuilder.bulk.summaryPutAwayPriority', { '0': putAwayPriorityNo ? getPriorityDescription(putAwayPriorityNo) : none }, 'Put-away Prioritet: {0}'));
        if (updateFields.negativeReservationAllowed) summary.push(t('manager.modules.rackBuilder.bulk.summaryNegativeReservation', { '0': negativeReservationAllowed ? yes : no }, 'Negativ reservasjon: {0}'));
        if (updateFields.sharedBinYesNo) summary.push(t('manager.modules.rackBuilder.bulk.summarySharedBin', { '0': sharedBinYesNo ? yes : no }, 'Delt lokasjon: {0}'));
        if (updateFields.sharedBinMaxQuantity) summary.push(t('manager.modules.rackBuilder.bulk.summarySharedBinMaxQuantity', { '0': sharedBinMaxQuantity }, 'Maks antall delt: {0}'));
        if (updateFields.sharedBinPreferEmptyBin) summary.push(t('manager.modules.rackBuilder.bulk.summaryPreferEmptyBin', { '0': sharedBinPreferEmptyBin ? yes : no }, 'Foretrekk tom lokasjon: {0}'));
        if (updateFields.barCodeConfirmLocation) summary.push(t('manager.modules.rackBuilder.bulk.summaryBarCodeConfirmLocation', {
            '0': barCodeConfirmLocation === 'true' ? yes : barCodeConfirmLocation === 'false' ? no : t('manager.modules.rackBuilder.bulk.scanFlagInherit', {}, 'Arv (fra profil)')
        }, 'Krev lokasjonsskann: {0}'));

        return summary;
    };

    // ===== Før/etter-forhåndsvisning per bin + angre-grunnlag (Fase 2) =====

    /** Ny verdi for et felt slik den vil bli sendt i requesten */
    const getAfterValue = (field: keyof UpdateFields): string | number | boolean | undefined => {
        switch (field) {
            case 'binSizeNo': return binSizeNo;
            case 'binTypeNo': return binTypeNo;
            case 'resourceNo': return resourceNo;
            case 'binGrpNo': return binGrpNo;
            case 'stcBinCatNo': return stcBinCatNo;
            case 'stcZoneID': return stcZoneID;
            case 'pickPriorityNo': return pickPriorityNo;
            case 'pickableYesNo': return pickableYesNo;
            case 'reservationAllowed': return reservationAllowed;
            case 'autoReplenishment': return autoReplenishment;
            case 'itemNo': return itemNo;
            case 'replenishMinLevel': return replenishMinLevel;
            case 'replenishMaxLevel': return replenishMaxLevel;
            case 'customSortID': return customSortID;
            case 'putAwayPriorityNo': return putAwayPriorityNo;
            case 'negativeReservationAllowed': return negativeReservationAllowed;
            case 'sharedBinYesNo': return sharedBinYesNo;
            case 'sharedBinMaxQuantity': return sharedBinMaxQuantity;
            case 'sharedBinPreferEmptyBin': return sharedBinPreferEmptyBin;
            case 'barCodeConfirmLocation': return barCodeConfirmLocation;
            default: return undefined;
        }
    };

    /** Nåværende verdi fra lokasjonen. barCodeConfirmLocation finnes ikke i BinLocation → ukjent. */
    const getBeforeValue = (loc: BinLocation, field: keyof UpdateFields): string | number | boolean | undefined => {
        if (field === 'barCodeConfirmLocation') return undefined;
        return (loc as unknown as Record<string, string | number | boolean | undefined>)[field];
    };

    const getFieldLabel = (field: keyof UpdateFields): string => {
        switch (field) {
            case 'binSizeNo': return t('manager.modules.rackBuilder.fields.binSize', {}, 'Bin Storrelse');
            case 'binTypeNo': return t('manager.modules.rackBuilder.fields.binType', {}, 'Bin Type');
            case 'resourceNo': return t('manager.modules.rackBuilder.fields.resource', {}, 'Ressurs');
            case 'binGrpNo': return t('manager.modules.rackBuilder.fields.binGroup', {}, 'Bin Gruppe');
            case 'stcBinCatNo': return t('manager.modules.rackBuilder.fields.binCategory', {}, 'Lokasjonskategori');
            case 'stcZoneID': return t('warehouse.general.zone', {}, 'Sone');
            case 'pickPriorityNo': return t('manager.modules.rackBuilder.fields.pickPriority', {}, 'Plukk Prioritet');
            case 'pickableYesNo': return t('manager.modules.rackBuilder.fields.pickable', {}, 'Plukkbar');
            case 'reservationAllowed': return t('manager.modules.rackBuilder.fields.reservation', {}, 'Reservasjon');
            case 'autoReplenishment': return t('manager.modules.rackBuilder.fields.autoReplenishment', {}, 'Auto-pafylling');
            case 'itemNo': return t('warehouse.general.itemNumber', {}, 'Varenummer');
            case 'replenishMinLevel': return t('manager.modules.rackBuilder.fields.minReplenishLevel', {}, 'Min pafyllniva');
            case 'replenishMaxLevel': return t('manager.modules.rackBuilder.fields.maxReplenishLevel', {}, 'Maks pafyllniva');
            case 'customSortID': return t('manager.modules.rackBuilder.fields.customSortId', {}, 'Sortering ID');
            case 'putAwayPriorityNo': return t('manager.modules.rackBuilder.fields.putAwayPriority', {}, 'Put-away Prioritet');
            case 'negativeReservationAllowed': return t('manager.modules.rackBuilder.fields.negativeReservation', {}, 'Negativ reservasjon');
            case 'sharedBinYesNo': return t('manager.modules.rackBuilder.fields.sharedBin', {}, 'Delt lokasjon');
            case 'sharedBinMaxQuantity': return t('manager.modules.rackBuilder.fields.sharedBinMaxQuantity', {}, 'Maks antall delt');
            case 'sharedBinPreferEmptyBin': return t('manager.modules.rackBuilder.fields.preferEmptyBin', {}, 'Foretrekk tom lokasjon');
            case 'barCodeConfirmLocation': return t('manager.modules.rackBuilder.fields.barCodeConfirmLocation', {}, 'Krev lokasjonsskann');
            default: return String(field);
        }
    };

    const formatFieldValue = (field: keyof UpdateFields, value: string | number | boolean | undefined): string => {
        const yes = t('manager.modules.rackBuilder.common.yes', {}, 'Ja');
        const no = t('manager.modules.rackBuilder.common.no', {}, 'Nei');
        if (value === undefined || value === null || value === '') {
            return t('manager.modules.rackBuilder.bulk.none', {}, 'Ingen');
        }
        switch (field) {
            case 'binSizeNo': return getBinSizeDescription(value as number);
            case 'binTypeNo': return getBinTypeDescription(value as number);
            case 'binGrpNo': return getBinGrpDescription(value as number);
            case 'stcBinCatNo': return getBinCategoryDescription(value as number);
            case 'stcZoneID': return getZoneDescription(value as string);
            case 'pickPriorityNo':
            case 'putAwayPriorityNo': return getPriorityDescription(value as number);
            case 'resourceNo': return getResourceDescription(value as number);
            case 'barCodeConfirmLocation':
                return value === 'true' ? yes : value === 'false' ? no : t('manager.modules.rackBuilder.bulk.scanFlagInherit', {}, 'Arv (fra profil)');
            default:
                if (typeof value === 'boolean') return value ? yes : no;
                return String(value);
        }
    };

    /** Felt brukeren har huket av for oppdatering */
    const getActiveFields = (): (keyof UpdateFields)[] =>
        (Object.keys(updateFields) as (keyof UpdateFields)[]).filter(f => updateFields[f]);

    /**
     * Per-bin før→etter for forhåndsvisningen i bekreftelsesdialogen.
     * Returnerer null når vi ikke har lokasjonsdata (fallback: gammel bincode-liste).
     */
    const buildPerBinChanges = (): { binCode: string; changes: string[] }[] | null => {
        if (!selectedLocations || selectedLocations.length === 0) return null;
        const selectedSet = new Set(selectedBinCodes);
        const activeFields = getActiveFields();
        const rows: { binCode: string; changes: string[] }[] = [];

        for (const loc of selectedLocations) {
            if (!selectedSet.has(loc.binCode)) continue;
            const changes: string[] = [];
            for (const field of activeFields) {
                const before = getBeforeValue(loc, field);
                const after = getAfterValue(field);
                if (field === 'barCodeConfirmLocation') {
                    // Før-verdi ukjent klientside — vis kun ny verdi
                    changes.push(`${getFieldLabel(field)}: ? → ${formatFieldValue(field, after)}`);
                    continue;
                }
                if (String(before ?? '') !== String(after ?? '')) {
                    changes.push(`${getFieldLabel(field)}: ${formatFieldValue(field, before)} → ${formatFieldValue(field, after)}`);
                }
            }
            rows.push({ binCode: loc.binCode, changes });
        }
        return rows;
    };

    /** Angre-grunnlag: før-verdier per bin for feltene som oppdateres (kun kjente, ikke-null verdier) */
    const buildUndoData = (): BulkUndoData | null => {
        if (!selectedLocations || selectedLocations.length === 0) return null;
        const activeFields = getActiveFields();
        const nonRevertible = activeFields.filter(f => f === 'barCodeConfirmLocation');
        const revertible = activeFields.filter(f => f !== 'barCodeConfirmLocation');
        const selectedSet = new Set(selectedBinCodes);

        const entries: BulkUndoEntry[] = [];
        for (const loc of selectedLocations) {
            if (!selectedSet.has(loc.binCode)) continue;
            const before: BulkUndoEntry['before'] = {};
            for (const field of revertible) {
                const value = getBeforeValue(loc, field);
                if (value !== undefined && value !== null) {
                    (before as Record<string, string | number | boolean>)[field] = value;
                }
            }
            if (Object.keys(before).length > 0) {
                entries.push({ binCode: loc.binCode, before });
            }
        }
        if (entries.length === 0) return null;

        return {
            timestamp: new Date().toISOString(),
            warehouseID,
            fieldsUpdated: activeFields as string[],
            entries,
            nonRevertibleFields: nonRevertible as string[]
        };
    };

    // Handle update operation
    const handleUpdate = async () => {
        if (!hasSelectedFields()) {
            setNotification({
                type: 'error',
                message: t('manager.modules.rackBuilder.bulk.selectAtLeastOneField', {}, 'Du ma velge minst ett felt som skal oppdateres')
            });
            return;
        }

        setShowUpdateConfirm(true);
    };

    // Confirm and execute update
    const executeUpdate = async () => {
        setIsUpdating(true);
        setShowUpdateConfirm(false);
        setNotification(null);

        // Snapshot av før-verdier FØR oppdateringen — grunnlaget for «Angre siste masseendring»
        const undoData = buildUndoData();

        try {
            const request = buildUpdateRequest();
            const response = await stcBinService.bulkUpdateBins(request);

            // Angre-data gjelder kun bins som faktisk ble oppdatert
            if (undoData && onUpdateApplied && response.successCount > 0) {
                const successSet = new Set(response.successfulBinCodes ?? []);
                const applied = (response.successfulBinCodes?.length ?? 0) > 0
                    ? { ...undoData, entries: undoData.entries.filter(e => successSet.has(e.binCode)) }
                    : undoData;
                if (applied.entries.length > 0) {
                    onUpdateApplied(applied);
                }
            }

            if (response.errors.length > 0) {
                setNotification({
                    type: 'warning',
                    message: t('manager.modules.rackBuilder.bulk.updatePartial', { '0': response.successCount, '1': selectedBinCodes.length, '2': response.errors.length }, 'Oppdatert {0} av {1} lokasjoner. {2} feil oppstod.')
                });
            } else {
                setNotification({
                    type: 'success',
                    message: t('manager.modules.rackBuilder.bulk.updateSuccess', { '0': response.successCount }, '{0} lokasjon(er) oppdatert')
                });
            }

            // Refresh and clear selection
            onUpdateSuccess();
        } catch (err) {
            setNotification({
                type: 'error',
                message: err instanceof Error ? err.message : t('manager.modules.rackBuilder.bulk.updateFailed', {}, 'Oppdatering feilet')
            });
        } finally {
            setIsUpdating(false);
        }
    };

    // Handle delete operation
    const handleDelete = () => {
        setShowDeleteConfirm(true);
    };

    // Confirm and execute delete
    const executeDelete = async () => {
        setIsDeleting(true);
        setShowDeleteConfirm(false);
        setNotification(null);

        try {
            const request: BulkDeleteBinsRequest = {
                companyNo,
                warehouseID,
                binCodes: selectedBinCodes,
                forceDelete: false
            };

            const response = await stcBinService.bulkDeleteBins(request);

            if (response.errors.length > 0) {
                setNotification({
                    type: 'warning',
                    message: t('manager.modules.rackBuilder.bulk.deletePartial', { '0': response.successCount, '1': selectedBinCodes.length, '2': response.errors.length }, 'Slettet {0} av {1} lokasjoner. {2} kunne ikke slettes (trolig med saldo).')
                });
            } else {
                setNotification({
                    type: 'success',
                    message: t('manager.modules.rackBuilder.bulk.deleteSuccess', { '0': response.successCount }, '{0} lokasjon(er) slettet')
                });
            }

            // Refresh and clear selection
            onDeleteSuccess();
        } catch (err) {
            setNotification({
                type: 'error',
                message: err instanceof Error ? err.message : t('manager.modules.rackBuilder.bulk.deleteFailed', {}, 'Sletting feilet')
            });
        } finally {
            setIsDeleting(false);
        }
    };

    if (!isOpen) return null;

    const tabs: { id: TabType; label: string }[] = [
        { id: 'basics', label: t('manager.modules.rackBuilder.bulk.tabBasics', {}, 'Grunnleggende') },
        { id: 'priority', label: t('manager.modules.rackBuilder.bulk.tabPriority', {}, 'Prioritet & Plukk') },
        { id: 'reservation', label: t('manager.modules.rackBuilder.fields.reservation', {}, 'Reservasjon') },
        { id: 'replenishment', label: t('manager.modules.rackBuilder.bulk.tabReplenishment', {}, 'Påfylling') },
        { id: 'shared', label: t('manager.modules.rackBuilder.fields.sharedBin', {}, 'Delt lokasjon') }
    ];

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                        <Settings className="w-6 h-6 text-blue-600" />
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">{t('manager.modules.rackBuilder.bulk.title', {}, 'Masseoppdatering')}</h2>
                            <p className="text-sm text-gray-600 mt-1">
                                {t('manager.modules.rackBuilder.bulk.locationsSelected', { '0': selectedBinCodes.length }, '{0} lokasjon(er) valgt')}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Notification */}
                {notification && (
                    <div className={`mx-6 mt-4 rounded-lg p-3 flex items-start space-x-2 ${
                        notification.type === 'success' ? 'bg-green-50 border border-green-200' :
                        notification.type === 'error' ? 'bg-red-50 border border-red-200' :
                        'bg-yellow-50 border border-yellow-200'
                    }`}>
                        {notification.type === 'success' && <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />}
                        {notification.type === 'error' && <X className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />}
                        {notification.type === 'warning' && <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />}
                        <p className={`text-sm flex-1 ${
                            notification.type === 'success' ? 'text-green-800' :
                            notification.type === 'error' ? 'text-red-800' :
                            'text-yellow-800'
                        }`}>
                            {notification.message}
                        </p>
                        <button
                            onClick={() => setNotification(null)}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}

                {/* Tab Navigation */}
                <div className="border-b border-gray-200 px-6">
                    <div className="flex space-x-8">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                                    activeTab === tab.id
                                        ? 'border-blue-600 text-blue-600'
                                        : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Tab Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    <div className="space-y-4">
                        {/* Tab 1: Grunnleggende (Basics) */}
                        {activeTab === 'basics' && (
                            <>
                                {/* BinSizeNo */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.binSizeNo}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, binSizeNo: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.rackBuilder.fields.binSize', {}, 'Bin Storrelse')}</span>
                                    </label>
                                    <select
                                        value={binSizeNo}
                                        onChange={(e) => setBinSizeNo(parseInt(e.target.value))}
                                        disabled={!updateFields.binSizeNo || isLoadingBinSizes}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    >
                                        {isLoadingBinSizes ? (
                                            <option>{t('common.status.loading', {}, 'Laster...')}</option>
                                        ) : binSizes.length > 0 ? (
                                            binSizes.map(size => (
                                                <option key={size.binSizeNo} value={size.binSizeNo}>
                                                    {size.binSizeNo} - {size.description1}
                                                </option>
                                            ))
                                        ) : filterOptions?.binSizeNo && filterOptions.binSizeNo.length > 0 ? (
                                            filterOptions.binSizeNo.map(value => (
                                                <option key={value} value={value}>{value}</option>
                                            ))
                                        ) : (
                                            <option value={binSizeNo}>{binSizeNo}</option>
                                        )}
                                    </select>
                                </div>

                                {/* BinTypeNo */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.binTypeNo}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, binTypeNo: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.rackBuilder.fields.binType', {}, 'Bin Type')}</span>
                                    </label>
                                    <select
                                        value={binTypeNo}
                                        onChange={(e) => setBinTypeNo(parseInt(e.target.value))}
                                        disabled={!updateFields.binTypeNo || isLoadingBinTypes}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    >
                                        {isLoadingBinTypes ? (
                                            <option>{t('common.status.loading', {}, 'Laster...')}</option>
                                        ) : binTypes.length > 0 ? (
                                            binTypes.map(type => (
                                                <option key={type.binTypeNo} value={type.binTypeNo}>
                                                    {type.binTypeNo} - {type.description1 || 'Bin Type'}
                                                </option>
                                            ))
                                        ) : filterOptions?.binTypeNo && filterOptions.binTypeNo.length > 0 ? (
                                            filterOptions.binTypeNo.map(value => (
                                                <option key={value} value={value}>{value}</option>
                                            ))
                                        ) : (
                                            <option value={binTypeNo}>{binTypeNo}</option>
                                        )}
                                    </select>
                                </div>

                                {/* ResourceNo */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.resourceNo}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, resourceNo: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.rackBuilder.fields.resource', {}, 'Ressurs')}</span>
                                    </label>
                                    <select
                                        value={resourceNo || ''}
                                        onChange={(e) => setResourceNo(e.target.value ? parseInt(e.target.value) : undefined)}
                                        disabled={!updateFields.resourceNo || isLoadingResources}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    >
                                        <option value="">{t('manager.modules.rackBuilder.bulk.none', {}, 'Ingen')}</option>
                                        {isLoadingResources ? (
                                            <option>{t('common.status.loading', {}, 'Laster...')}</option>
                                        ) : resources.length > 0 ? (
                                            resources.map(resource => (
                                                <option key={resource.resourceNo} value={resource.resourceNo}>
                                                    {resource.resourceNo} - {resource.description1}
                                                </option>
                                            ))
                                        ) : filterOptions?.resourceNo && filterOptions.resourceNo.length > 0 ? (
                                            filterOptions.resourceNo.map(value => (
                                                <option key={value} value={value}>{value}</option>
                                            ))
                                        ) : null}
                                    </select>
                                </div>

                                {/* BinGrpNo */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.binGrpNo}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, binGrpNo: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.rackBuilder.fields.binGroup', {}, 'Bin Gruppe')}</span>
                                    </label>
                                    <select
                                        value={binGrpNo || ''}
                                        onChange={(e) => setBinGrpNo(e.target.value ? parseInt(e.target.value) : undefined)}
                                        disabled={!updateFields.binGrpNo || isLoadingBinGroups}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    >
                                        <option value="">{t('manager.modules.rackBuilder.bulk.none', {}, 'Ingen')}</option>
                                        {isLoadingBinGroups ? (
                                            <option>{t('common.status.loading', {}, 'Laster...')}</option>
                                        ) : binGroups.length > 0 ? (
                                            binGroups.map(group => (
                                                <option key={group.binGrpNo} value={group.binGrpNo}>
                                                    {group.binGrpNo} - {group.description1}
                                                </option>
                                            ))
                                        ) : filterOptions?.binGrpNo && filterOptions.binGrpNo.length > 0 ? (
                                            filterOptions.binGrpNo.map(value => (
                                                <option key={value} value={value}>{value}</option>
                                            ))
                                        ) : null}
                                    </select>
                                </div>

                                {/* StcBinCatNo */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.stcBinCatNo}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, stcBinCatNo: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.rackBuilder.fields.binCategory', {}, 'Lokasjonskategori')}</span>
                                    </label>
                                    <select
                                        value={stcBinCatNo || ''}
                                        onChange={(e) => setStcBinCatNo(e.target.value ? parseInt(e.target.value) : undefined)}
                                        disabled={!updateFields.stcBinCatNo}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    >
                                        <option value="">{t('manager.modules.rackBuilder.bulk.none', {}, 'Ingen')}</option>
                                        {binCategories.length > 0 ? (
                                            binCategories.map(category => (
                                                <option key={category.stcBinCatNo} value={category.stcBinCatNo}>
                                                    {category.stcBinCatNo} - {category.lng_1}
                                                </option>
                                            ))
                                        ) : filterOptions?.stcBinCatNo && filterOptions.stcBinCatNo.length > 0 ? (
                                            filterOptions.stcBinCatNo.map(value => (
                                                <option key={value} value={value}>{value}</option>
                                            ))
                                        ) : null}
                                    </select>
                                </div>

                                {/* StcZoneID */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.stcZoneID}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, stcZoneID: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('warehouse.general.zone', {}, 'Sone')}</span>
                                    </label>
                                    <select
                                        value={stcZoneID}
                                        onChange={(e) => setStcZoneID(e.target.value)}
                                        disabled={!updateFields.stcZoneID || isLoadingZones}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    >
                                        {isLoadingZones ? (
                                            <option>{t('common.status.loading', {}, 'Laster...')}</option>
                                        ) : zones.length > 0 ? (
                                            zones.map(zone => (
                                                <option key={zone.stcZoneID} value={zone.stcZoneID}>
                                                    {zone.stcZoneID} - {zone.description1}
                                                </option>
                                            ))
                                        ) : filterOptions?.stcZoneID && filterOptions.stcZoneID.length > 0 ? (
                                            filterOptions.stcZoneID.map(value => (
                                                <option key={value} value={value}>{value}</option>
                                            ))
                                        ) : (
                                            <option value={stcZoneID}>{stcZoneID}</option>
                                        )}
                                    </select>
                                </div>
                            </>
                        )}

                        {/* Tab 2: Prioritet & Plukk (Priority & Picking) */}
                        {activeTab === 'priority' && (
                            <>
                                {/* PickPriorityNo */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.pickPriorityNo}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, pickPriorityNo: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.rackBuilder.fields.pickPriority', {}, 'Plukk Prioritet')}</span>
                                    </label>
                                    <select
                                        value={pickPriorityNo || ''}
                                        onChange={(e) => setPickPriorityNo(e.target.value ? parseInt(e.target.value) : undefined)}
                                        disabled={!updateFields.pickPriorityNo || isLoadingPriorities}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    >
                                        <option value="">{t('manager.modules.rackBuilder.bulk.none', {}, 'Ingen')}</option>
                                        {isLoadingPriorities ? (
                                            <option>{t('common.status.loading', {}, 'Laster...')}</option>
                                        ) : priorities.length > 0 ? (
                                            priorities.map(priority => (
                                                <option key={priority.priorityNo} value={priority.priorityNo}>
                                                    {priority.priorityNo} - {priority.description1}
                                                </option>
                                            ))
                                        ) : filterOptions?.pickPriorityNo && filterOptions.pickPriorityNo.length > 0 ? (
                                            filterOptions.pickPriorityNo.map(value => (
                                                <option key={value} value={value}>{value}</option>
                                            ))
                                        ) : null}
                                    </select>
                                </div>

                                {/* PutAwayPriorityNo */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.putAwayPriorityNo}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, putAwayPriorityNo: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.rackBuilder.fields.putAwayPriority', {}, 'Put-away Prioritet')}</span>
                                    </label>
                                    <select
                                        value={putAwayPriorityNo || ''}
                                        onChange={(e) => setPutAwayPriorityNo(e.target.value ? parseInt(e.target.value) : undefined)}
                                        disabled={!updateFields.putAwayPriorityNo || isLoadingPriorities}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    >
                                        <option value="">{t('manager.modules.rackBuilder.bulk.none', {}, 'Ingen')}</option>
                                        {isLoadingPriorities ? (
                                            <option>{t('common.status.loading', {}, 'Laster...')}</option>
                                        ) : priorities.length > 0 ? (
                                            priorities.map(priority => (
                                                <option key={priority.priorityNo} value={priority.priorityNo}>
                                                    {priority.priorityNo} - {priority.description1}
                                                </option>
                                            ))
                                        ) : null}
                                    </select>
                                </div>

                                {/* PickableYesNo */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.pickableYesNo}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, pickableYesNo: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.rackBuilder.fields.pickable', {}, 'Plukkbar')}</span>
                                    </label>
                                    <label className="flex items-center space-x-3 px-3 py-2 border border-gray-300 rounded-lg bg-white">
                                        <input
                                            type="checkbox"
                                            checked={pickableYesNo}
                                            onChange={(e) => setPickableYesNo(e.target.checked)}
                                            disabled={!updateFields.pickableYesNo}
                                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-30"
                                        />
                                        <span className="text-sm text-gray-700">{t('manager.modules.rackBuilder.bulk.enablePickable', {}, 'Aktiver plukkbar')}</span>
                                    </label>
                                </div>

                                {/* BarCodeConfirmLocation — unntaksflagg lokasjonsskann (TW 36344429) */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.barCodeConfirmLocation}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, barCodeConfirmLocation: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.rackBuilder.fields.barCodeConfirmLocation', {}, 'Krev lokasjonsskann')}</span>
                                    </label>
                                    <select
                                        value={barCodeConfirmLocation}
                                        onChange={(e) => setBarCodeConfirmLocation(e.target.value)}
                                        disabled={!updateFields.barCodeConfirmLocation}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    >
                                        <option value="">{t('manager.modules.rackBuilder.bulk.scanFlagInherit', {}, 'Arv (fra profil)')}</option>
                                        <option value="true">{t('manager.modules.rackBuilder.common.yes', {}, 'Ja')}</option>
                                        <option value="false">{t('manager.modules.rackBuilder.common.no', {}, 'Nei')}</option>
                                    </select>
                                    <p className="text-xs text-gray-500">{t('manager.modules.rackBuilder.bulk.scanFlagHelp', {}, 'Overstyrer profilinnstillingen for disse lokasjonene i plukk og vareplassering. Arv = profilen bestemmer.')}</p>
                                </div>

                                {/* CustomSortID */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.customSortID}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, customSortID: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.rackBuilder.fields.customSortId', {}, 'Sortering ID')}</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={customSortID}
                                        onChange={(e) => setCustomSortID(parseInt(e.target.value) || 1000)}
                                        disabled={!updateFields.customSortID}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    />
                                </div>
                            </>
                        )}

                        {/* Tab 3: Reservasjon (Reservation) */}
                        {activeTab === 'reservation' && (
                            <>
                                {/* ReservationAllowed */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.reservationAllowed}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, reservationAllowed: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.rackBuilder.fields.reservation', {}, 'Reservasjon')}</span>
                                    </label>
                                    <label className="flex items-center space-x-3 px-3 py-2 border border-gray-300 rounded-lg bg-white">
                                        <input
                                            type="checkbox"
                                            checked={reservationAllowed}
                                            onChange={(e) => setReservationAllowed(e.target.checked)}
                                            disabled={!updateFields.reservationAllowed}
                                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-30"
                                        />
                                        <span className="text-sm text-gray-700">{t('manager.modules.rackBuilder.bulk.allowReservation', {}, 'Tillat reservasjon')}</span>
                                    </label>
                                </div>

                                {/* NegativeReservationAllowed */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.negativeReservationAllowed}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, negativeReservationAllowed: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.rackBuilder.fields.negativeReservation', {}, 'Negativ reservasjon')}</span>
                                    </label>
                                    <label className="flex items-center space-x-3 px-3 py-2 border border-gray-300 rounded-lg bg-white">
                                        <input
                                            type="checkbox"
                                            checked={negativeReservationAllowed}
                                            onChange={(e) => setNegativeReservationAllowed(e.target.checked)}
                                            disabled={!updateFields.negativeReservationAllowed}
                                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-30"
                                        />
                                        <span className="text-sm text-gray-700">{t('manager.modules.rackBuilder.bulk.allowNegativeReservation', {}, 'Tillat negativ reservasjon')}</span>
                                    </label>
                                </div>
                            </>
                        )}

                        {/* Tab 4: Påfylling (Replenishment) */}
                        {activeTab === 'replenishment' && (
                            <>
                                {/* AutoReplenishment */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.autoReplenishment}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, autoReplenishment: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.rackBuilder.fields.autoReplenishment', {}, 'Auto-pafylling')}</span>
                                    </label>
                                    <label className="flex items-center space-x-3 px-3 py-2 border border-gray-300 rounded-lg bg-white">
                                        <input
                                            type="checkbox"
                                            checked={autoReplenishment}
                                            onChange={(e) => setAutoReplenishment(e.target.checked)}
                                            disabled={!updateFields.autoReplenishment}
                                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-30"
                                        />
                                        <span className="text-sm text-gray-700">{t('manager.modules.rackBuilder.bulk.enableAutoReplenishment', {}, 'Aktiver auto-pafylling')}</span>
                                    </label>
                                </div>

                                {/* ReplenishMinLevel */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.replenishMinLevel}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, replenishMinLevel: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.rackBuilder.fields.minReplenishLevel', {}, 'Min pafyllniva')}</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={replenishMinLevel}
                                        onChange={(e) => setReplenishMinLevel(parseFloat(e.target.value) || 0)}
                                        disabled={!updateFields.replenishMinLevel}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    />
                                </div>

                                {/* ReplenishMaxLevel */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.replenishMaxLevel}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, replenishMaxLevel: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.rackBuilder.fields.maxReplenishLevel', {}, 'Maks pafyllniva')}</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={replenishMaxLevel}
                                        onChange={(e) => setReplenishMaxLevel(parseFloat(e.target.value) || 0)}
                                        disabled={!updateFields.replenishMaxLevel}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    />
                                </div>

                                {/* ItemNo - WITH SEARCH! */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.itemNo}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, itemNo: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('warehouse.general.itemNumber', {}, 'Varenummer')}</span>
                                    </label>
                                    <div className="relative">
                                        <div className="relative">
                                            <input
                                                type="text"
                                                value={itemSearchTerm}
                                                onChange={(e) => setItemSearchTerm(e.target.value)}
                                                onFocus={() => {
                                                    if (itemSearchResults.length > 0) {
                                                        setShowItemDropdown(true);
                                                    }
                                                }}
                                                disabled={!updateFields.itemNo}
                                                placeholder={t('manager.modules.rackBuilder.bulk.itemSearchPlaceholder', {}, 'Sok varenummer, beskrivelse eller GTIN...')}
                                                className="w-full px-3 py-2 pr-10 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                            />
                                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                                {isSearchingItems ? (
                                                    <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                                                ) : selectedItem ? (
                                                    <button
                                                        type="button"
                                                        onClick={handleClearItem}
                                                        className="pointer-events-auto text-gray-400 hover:text-gray-600"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                ) : (
                                                    <Search className="w-4 h-4 text-gray-400" />
                                                )}
                                            </div>
                                        </div>

                                        {/* Search results dropdown */}
                                        {showItemDropdown && itemSearchResults.length > 0 && (
                                            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                                {itemSearchResults.map((item) => (
                                                    <button
                                                        key={item.itemNo}
                                                        type="button"
                                                        onClick={() => handleSelectItem(item)}
                                                        className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-b border-gray-100 last:border-b-0"
                                                    >
                                                        <div className="font-medium text-gray-900">{item.itemNo}</div>
                                                        <div className="text-xs text-gray-600 truncate">{item.description1}</div>
                                                        {item.itemBarCode && (
                                                            <div className="text-xs text-gray-500">GTIN: {item.itemBarCode}</div>
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        )}

                                        {/* Selected item display */}
                                        {selectedItem && (
                                            <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                                                <div className="font-medium text-blue-900">{selectedItem.itemNo}</div>
                                                <div className="text-blue-700">{selectedItem.description1}</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Tab 5: Delt lokasjon (Shared Location) */}
                        {activeTab === 'shared' && (
                            <>
                                {/* SharedBinYesNo */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.sharedBinYesNo}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, sharedBinYesNo: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.rackBuilder.fields.sharedBin', {}, 'Delt lokasjon')}</span>
                                    </label>
                                    <label className="flex items-center space-x-3 px-3 py-2 border border-gray-300 rounded-lg bg-white">
                                        <input
                                            type="checkbox"
                                            checked={sharedBinYesNo}
                                            onChange={(e) => setSharedBinYesNo(e.target.checked)}
                                            disabled={!updateFields.sharedBinYesNo}
                                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-30"
                                        />
                                        <span className="text-sm text-gray-700">{t('manager.modules.rackBuilder.bulk.enableSharedBin', {}, 'Aktiver delt lokasjon')}</span>
                                    </label>
                                </div>

                                {/* SharedBinMaxQuantity */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.sharedBinMaxQuantity}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, sharedBinMaxQuantity: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.rackBuilder.fields.sharedBinMaxQuantity', {}, 'Maks antall delt')}</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={sharedBinMaxQuantity}
                                        onChange={(e) => setSharedBinMaxQuantity(parseInt(e.target.value) || 0)}
                                        disabled={!updateFields.sharedBinMaxQuantity}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    />
                                </div>

                                {/* SharedBinPreferEmptyBin */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.sharedBinPreferEmptyBin}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, sharedBinPreferEmptyBin: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.rackBuilder.fields.preferEmptyBin', {}, 'Foretrekk tom lokasjon')}</span>
                                    </label>
                                    <label className="flex items-center space-x-3 px-3 py-2 border border-gray-300 rounded-lg bg-white">
                                        <input
                                            type="checkbox"
                                            checked={sharedBinPreferEmptyBin}
                                            onChange={(e) => setSharedBinPreferEmptyBin(e.target.checked)}
                                            disabled={!updateFields.sharedBinPreferEmptyBin}
                                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-30"
                                        />
                                        <span className="text-sm text-gray-700">{t('manager.modules.rackBuilder.bulk.preferEmptyBins', {}, 'Foretrekk tomme lokasjoner')}</span>
                                    </label>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 p-6 bg-gray-50">
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-600">
                            {t('manager.modules.rackBuilder.bulk.locationsSelectedPlain', { '0': selectedBinCodes.length }, '{0} lokasjoner valgt')}
                        </div>
                        <div className="flex items-center space-x-3">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                {t('common.buttons.cancel', {}, 'Avbryt')}
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={isUpdating || isDeleting}
                                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isDeleting ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Trash2 className="w-4 h-4" />
                                )}
                                <span>{t('manager.modules.rackBuilder.bulk.deleteSelected', {}, 'Slett valgte')}</span>
                            </button>
                            <button
                                onClick={handleUpdate}
                                disabled={!hasSelectedFields() || isUpdating || isDeleting}
                                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isUpdating ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Save className="w-4 h-4" />
                                )}
                                <span>{t('common.buttons.refresh', {}, 'Oppdater')}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Update confirmation dialog */}
            {showUpdateConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto">
                        <div className="p-6 space-y-4">
                            <div className="flex items-start space-x-3">
                                <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
                                <div className="flex-1">
                                    <h3 className="text-lg font-bold text-gray-900">{t('manager.modules.rackBuilder.bulk.confirmUpdateTitle', {}, 'Bekreft oppdatering')}</h3>
                                    <p className="text-sm text-gray-600 mt-1">
                                        {t('manager.modules.rackBuilder.bulk.aboutToUpdate', { '0': selectedBinCodes.length }, 'Du er i ferd med a oppdatere {0} lokasjon(er)')}
                                    </p>
                                </div>
                            </div>

                            {/* Fields to update */}
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <p className="text-sm font-medium text-blue-900 mb-2">{t('manager.modules.rackBuilder.bulk.fieldsToUpdate', {}, 'Felt som vil bli oppdatert:')}</p>
                                <ul className="text-sm text-blue-800 space-y-1">
                                    {getUpdateSummary().map((item, idx) => (
                                        <li key={idx}>• {item}</li>
                                    ))}
                                </ul>
                            </div>

                            {/* Per-bin før → etter (fallback: ren bincode-liste når lokasjonsdata mangler) */}
                            {(() => {
                                const perBin = buildPerBinChanges();
                                if (!perBin) {
                                    return (
                                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                                            <p className="text-sm font-medium text-gray-900 mb-2">{t('manager.modules.rackBuilder.bulk.locationsLabel', {}, 'Lokasjoner:')}</p>
                                            <div className="max-h-40 overflow-y-auto">
                                                <div className="text-xs text-gray-700 space-y-1">
                                                    {selectedBinCodes.slice(0, 20).map((code, idx) => (
                                                        <div key={idx} className="font-mono">{code}</div>
                                                    ))}
                                                    {selectedBinCodes.length > 20 && (
                                                        <div className="text-gray-500 italic">
                                                            {t('manager.modules.rackBuilder.bulk.andMore', { '0': selectedBinCodes.length - 20 }, '... og {0} flere')}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }

                                const changedCount = perBin.filter(r => r.changes.length > 0).length;
                                const unchangedCount = perBin.length - changedCount;
                                return (
                                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                                        <p className="text-sm font-medium text-gray-900 mb-2">
                                            {t('manager.modules.rackBuilder.bulk.previewTitle', { '0': changedCount }, 'Endringer per lokasjon ({0} berøres):')}
                                            {unchangedCount > 0 && (
                                                <span className="text-gray-500 font-normal ml-2">
                                                    {t('manager.modules.rackBuilder.bulk.previewUnchanged', { '0': unchangedCount }, '{0} har allerede disse verdiene')}
                                                </span>
                                            )}
                                        </p>
                                        <div className="max-h-48 overflow-y-auto">
                                            <div className="text-xs text-gray-700 space-y-1.5">
                                                {perBin.slice(0, 20).map(row => (
                                                    <div key={row.binCode}>
                                                        <span className="font-mono font-semibold">{row.binCode}</span>
                                                        {row.changes.length === 0 ? (
                                                            <span className="text-gray-400 ml-2 italic">
                                                                {t('manager.modules.rackBuilder.bulk.previewNoChange', {}, '(uendret)')}
                                                            </span>
                                                        ) : (
                                                            <div className="ml-3 text-gray-600">
                                                                {row.changes.map((c, idx) => (
                                                                    <div key={idx}>{c}</div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                                {perBin.length > 20 && (
                                                    <div className="text-gray-500 italic">
                                                        {t('manager.modules.rackBuilder.bulk.andMore', { '0': perBin.length - 20 }, '... og {0} flere')}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>

                        <div className="flex items-center justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50">
                            <button
                                onClick={() => setShowUpdateConfirm(false)}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                {t('common.buttons.cancel', {}, 'Avbryt')}
                            </button>
                            <button
                                onClick={executeUpdate}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                {t('manager.modules.rackBuilder.bulk.confirmUpdateButton', {}, 'Bekreft oppdatering')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete confirmation dialog */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
                        <div className="p-6 space-y-4">
                            <div className="flex items-start space-x-3">
                                <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                                <div className="flex-1">
                                    <h3 className="text-lg font-bold text-gray-900">{t('manager.modules.rackBuilder.bulk.confirmDeleteTitle', {}, 'Bekreft sletting')}</h3>
                                    <p className="text-sm text-gray-600 mt-1">
                                        {t('manager.modules.rackBuilder.bulk.aboutToDelete', { '0': selectedBinCodes.length }, 'Du er i ferd med a slette {0} lokasjon(er)')}
                                    </p>
                                </div>
                            </div>

                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <p className="text-sm text-red-800">
                                    <strong>{t('manager.modules.rackBuilder.bulk.warningLabel', {}, 'Advarsel:')}</strong> {t('manager.modules.rackBuilder.bulk.deleteWarning', {}, 'Denne handlingen kan ikke angres. Lokasjoner med saldo vil ikke bli slettet.')}
                                </p>
                            </div>

                            {/* BinCodes list */}
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                                <p className="text-sm font-medium text-gray-900 mb-2">{t('manager.modules.rackBuilder.bulk.locationsToDelete', {}, 'Lokasjoner som vil slettes:')}</p>
                                <div className="max-h-40 overflow-y-auto">
                                    <div className="text-xs text-gray-700 space-y-1">
                                        {selectedBinCodes.slice(0, 20).map((code, idx) => (
                                            <div key={idx} className="font-mono">{code}</div>
                                        ))}
                                        {selectedBinCodes.length > 20 && (
                                            <div className="text-gray-500 italic">
                                                {t('manager.modules.rackBuilder.bulk.andMore', { '0': selectedBinCodes.length - 20 }, '... og {0} flere')}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50">
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                {t('common.buttons.cancel', {}, 'Avbryt')}
                            </button>
                            <button
                                onClick={executeDelete}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                            >
                                {t('manager.modules.rackBuilder.bulk.confirmDeleteButton', {}, 'Ja, slett')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
